// Traducción informativa al inglés de las páginas legales. La versión
// jurídicamente vinculante es la española (la página ya muestra esa nota).
import { business } from "../site";

export const legalEn = {
  "aviso-legal": {
    title: "Legal notice",
    seoTitle: "Legal notice | MideFinca",
    metaDescription: "MideFinca legal notice: site owner, terms of use, intellectual property and applicable law.",
    sections: [
      {
        h2: "Website owner",
        paragraphs: [
          "In compliance with article 10 of Spanish Act 34/2002 on Information Society Services and Electronic Commerce (LSSI-CE), the owner of this website is identified as follows:",
          `Owner: ${business.legalName}`,
          `Tax ID (NIF): ${business.nif}`,
          `Address: ${business.legalAddress}`,
          `Email: ${business.email} · Phone: ${business.phoneDisplay}`,
          "Domain: https://midefinca.es",
        ],
      },
      {
        h2: "Information on the practice of a regulated profession",
        paragraphs: [
          "The technical direction of the services offered corresponds to the practice of a regulated profession, with the following details:",
        ],
        list: [
          "Profession: Architect",
          "Official degree: Architect",
          "State in which the degree was issued: Spain",
          "Professional body: Official College of Architects of Tenerife, La Gomera and El Hierro",
          "Membership number: 3665",
        ],
      },
      {
        h2: "Applicable professional rules",
        paragraphs: [
          "The practice of the profession is governed by the following professional and deontological rules:",
        ],
        list: [
          "General Statutes of the Official Colleges of Architects and their Higher Council.",
          "Statute of the Canary Islands Council of Colleges of Architects.",
          "Particular Statute of the Official College of Architects of Tenerife, La Gomera and El Hierro.",
          "Code of Ethics of Architects.",
        ],
        after: [
          {
            text: "These rules can be consulted electronically in the regulations section of the Official College of Architects of Tenerife, La Gomera and El Hierro (COACTFE): ",
            href: "https://www.coactfe.org/institucional",
            linkText: "https://www.coactfe.org/institucional",
          },
        ],
      },
      {
        h2: "Purpose of the website",
        paragraphs: [
          "midefinca.es provides information and technical services related to the surveying of plots and land, topographic surveys, georeferencing, GML files and graphic representation, technical work linked to Catastro and the Land Registry, checking of surfaces, boundaries and demarcations, technical certificates and, in general, technical documentation of land and properties.",
        ],
      },
      {
        h2: "Terms of use",
        paragraphs: [
          "Access to and use of this website confers the status of user and implies acceptance of these terms. The user undertakes to make lawful, diligent, honest and correct use of the site, in accordance with the applicable legislation, good faith and public order, and not to use it to damage its availability, its security or the rights of the owner or of third parties.",
        ],
      },
      {
        h2: "Technical information and outcome of procedures before third parties",
        paragraphs: [
          "The information published on this website, including the guides and articles, is general and informative in nature and does not replace the individual study of each property, plot, file or documentation.",
          "Matters related to Catastro, the Land Registry, notaries, town halls, graphic validations, correction procedures, administrative files, acceptance of graphic representations or coordination between Catastro and the Land Registry may depend on the existing documentation, on third parties, including the adjoining owners, and on the criteria or resolutions of the competent bodies. Consequently, commissioning technical work must not be interpreted as a guarantee that an administration, Catastro, the Land Registry, a notary or another body will resolve a procedure in a particular way.",
          "The foregoing is understood without prejudice to the liability corresponding to the provider for the technical work itself, in accordance with the legislation in force.",
        ],
      },
      {
        h2: "Data provided by the client or third parties",
        paragraphs: [
          "Technical work may be carried out on the basis of documentation or information provided by the client or by third parties. The owner shall not be liable for errors arising exclusively from false, incomplete, incorrect or outdated data supplied by the client or by third parties, always within the limits permitted by the applicable legislation.",
        ],
      },
      {
        h2: "Site content and currency of regulations",
        paragraphs: [
          "We strive to keep the information up to date; however, regulations, administrative procedures, technical criteria and formalities may change after each piece of content is published. The owner does not guarantee that the information reflects the regulations or administrative situation in force at all times. The guides and articles do not in themselves constitute individual technical advice and should not ground decisions without studying the specific case.",
        ],
      },
      {
        h2: "Intellectual and industrial property",
        paragraphs: [
          "The MideFinca trademark, the logo, the texts, graphics, plans, photographs, infographics, designs, the technical content and the structure and design of this website are owned by the owner or used with authorisation or licence. Their unauthorised reproduction, distribution, public communication, transformation or exploitation is prohibited, under the terms provided by intellectual and industrial property legislation.",
        ],
      },
      {
        h2: "Links to third-party sites",
        paragraphs: [
          "This site may include links to third-party sites (for example, Catastro, the Land Registry, public administrations or messaging services) for the user's convenience. The owner does not control and is not responsible for the content, availability or policies of such sites, without prejudice to any liability that may legally correspond.",
        ],
      },
      {
        h2: "Site availability and security",
        paragraphs: [
          "The owner adopts reasonable technical measures for the correct operation of the site. However, uninterrupted availability or the absence of errors cannot be guaranteed, and the owner shall not be liable for interruptions or damage arising from maintenance work, failures of third-party providers, cyberattacks or other causes beyond their reasonable control, within the limits permitted by the legislation in force.",
        ],
      },
      {
        h2: "Applicable law",
        paragraphs: [
          "This website and its terms are governed by Spanish law. For the resolution of disputes, the courts and tribunals resulting from the applicable legislation shall have jurisdiction; in particular, when the user has the status of consumer, the courts imperatively determined by consumer and user regulations shall have jurisdiction.",
        ],
      },
    ],
  },
  "politica-de-privacidad": {
    title: "Privacy policy",
    seoTitle: "Privacy policy | MideFinca",
    metaDescription: "MideFinca privacy policy: controller, data processed, purposes, legal bases, retention, recipients and rights (GDPR and Spanish LOPDGDD).",
    sections: [
      {
        h2: "Data controller",
        paragraphs: [
          `Controller: ${business.legalName} · Tax ID (NIF): ${business.nif} · Address: ${business.legalAddress}`,
          `Email: ${business.email} · Phone: ${business.phoneDisplay} · Website: https://midefinca.es`,
        ],
      },
      {
        h2: "Personal data processed",
        paragraphs: [
          "This website processes only the data necessary to handle the requests received. Specifically:",
        ],
        list: [
          "Contact and quote request form: name, phone, email, municipality, type of need, description of the case, cadastral reference (optional) and documentation voluntarily provided (PDF, JPG or PNG).",
          "Direct contact by phone, email or WhatsApp: the data the user provides in that communication. If you use WhatsApp, the communication takes place through the WhatsApp service (Meta), to which its own terms and privacy policy apply.",
          "Assignment management: the data necessary for the provision of the contracted service, the property documentation and invoicing.",
          "Browsing: the server's technical logs (IP address, date and pages requested), for security and operation purposes.",
          "This website does not use data for advertising or newsletters, and currently has no active analytics or tracking systems.",
        ],
      },
      {
        h2: "Purposes and legal bases",
        paragraphs: ["Each processing activity rests on its corresponding legal basis under article 6 of the GDPR:"],
        list: [
          "Answering enquiries, studying the case and preparing quotes: application of pre-contractual measures at the data subject's request (art. 6.1.b GDPR).",
          "Providing the contracted services and managing the professional relationship: performance of a contract (art. 6.1.b GDPR).",
          "Invoicing and compliance with tax, accounting and legal obligations: compliance with legal obligations (art. 6.1.c GDPR).",
          "Server security technical logs: legitimate interest in guaranteeing the site's security (art. 6.1.f GDPR).",
          "Web analytics, should it be activated in the future: consent (art. 6.1.a GDPR), requested beforehand and independently.",
        ],
      },
      {
        h2: "Retention periods",
        paragraphs: [
          "Data from enquiries that do not lead to an assignment will be kept for the time needed to handle the request and, as a general rule, a maximum of twelve months to be able to deal with later clarifications related to it, unless earlier erasure is requested.",
          "Client data and assignment documentation will be kept for the duration of the professional relationship and, once ended, for the periods required by tax, accounting and liability-limitation legislation (as a general rule, four years for tax matters and six for commercial and accounting matters).",
          "The server's technical logs are kept for short security periods, according to the hosting provider's usual configuration.",
        ],
      },
      {
        h2: "Data recipients",
        paragraphs: [
          "Personal data is not disclosed to third parties for commercial or advertising purposes. Data may only be communicated when necessary for the contracted service or where there is a legal obligation:",
        ],
        list: [
          "Public administrations, Catastro, the Land Registry, notaries or other intervening professionals, when the file or contracted service requires it.",
          "The tax administration and other bodies, when a legal obligation exists.",
          "Accountants or administrative advisers for compliance with tax and accounting obligations.",
          "Financial institutions for payment collection.",
        ],
      },
      {
        h2: "Data processors",
        paragraphs: [
          "The following providers with limited access to data take part in the technical operation of the site, acting as data processors:",
        ],
        list: [
          "The hosting and technical platform provider of the website and its database.",
          "The transactional email service used to send the form requests to the owner (Resend, through the Emergent platform).",
          "The owner's email provider (OVH), when communication takes place by email.",
        ],
      },
      {
        h2: "International data transfers",
        paragraphs: [
          "Some of the technology providers mentioned may process data outside the European Economic Area. In that case, the processing will be covered by the safeguard mechanisms provided by the GDPR, such as adequacy decisions or standard contractual clauses approved by the European Commission.",
        ],
      },
      {
        h2: "Rights of data subjects",
        paragraphs: [
          "You can exercise the rights of access, rectification, erasure, objection, restriction of processing and portability, as well as withdraw consent when it is the legal basis, by writing to " + business.email + " and stating the right you wish to exercise; if there were reasonable doubts about your identity, you could be asked for a supporting document.",
          "You also have the right to lodge a complaint with the Spanish Data Protection Agency (www.aepd.es).",
        ],
      },
      {
        h2: "Security measures",
        paragraphs: [
          "Technical and organisational measures appropriate to the risk are applied: TLS-encrypted communications, access control to the management area, limitation of the data collected to what is strictly necessary and validation of attached documents. However, no system is absolutely invulnerable; in the event of a security incident affecting your data, action will be taken in accordance with the legal notification obligations.",
        ],
      },
    ],
  },
  "politica-de-cookies": {
    title: "Cookie policy",
    seoTitle: "Cookie policy | MideFinca",
    metaDescription: "MideFinca cookie policy: real audit of cookies and local storage, consent and how to configure your choice.",
    sections: [
      {
        h2: "What cookies are",
        paragraphs: [
          "Cookies are small files that websites install in the browser. Similar technologies exist, such as local storage (localStorage), which serve similar functions. Some are essential for the website to work (technical or necessary) and are exempt from consent; others, such as analytics or advertising cookies, can only be installed if the user accepts them beforehand.",
        ],
      },
      {
        h2: "What this website actually uses",
        paragraphs: [
          "After technically auditing the site: midefinca.es currently installs no first-party or third-party cookies. There is no analytics, advertising, tracking pixels or embedded third-party maps or videos. The typefaces are self-hosted, so your visit sends no data to external font CDNs.",
        ],
      },
      {
        h2: "Analytics cookies",
        paragraphs: [
          "This website currently uses no analytics or measurement cookies: your visit is not recorded for statistical purposes.",
          "If a visit-statistics tool (for example, Google Analytics) were activated in the future, it would only be installed if you accept it beforehand from the cookie notice. In that case, this page would be updated to inform you which cookies would be used, for what purpose and for how long.",
          "Advertising or tracking cookies will never be installed without informing you beforehand on this page and without your prior consent.",
        ],
      },
      {
        h2: "Consent system",
        paragraphs: [
          "When non-exempt cookies exist, the website will show a first-layer notice with the options Accept, Reject and Settings, with equivalent visibility. Before your decision no non-essential technology is loaded; rejecting prevents their installation, and settings lets you decide by category. Your choice is stored in your browser and, while non-exempt cookies are active, you will find a «Cookie settings» link in the footer to modify or withdraw it as easily as you gave it.",
          "Continuing to browse, scrolling, closing the notice or staying on the page will not be considered consent.",
        ],
      },
      {
        h2: "How to delete cookies and local storage",
        paragraphs: [
          "You can delete or block cookies and local storage from your browser settings: Chrome (Settings → Privacy and security → Cookies), Firefox (Settings → Privacy & Security), Safari (Preferences → Privacy) and Edge (Settings → Cookies and site permissions). Deleting this site's data also removes the stored preference, and the notice will be shown again if non-exempt cookies are active.",
        ],
      },
      {
        h2: "Third-party processing and international transfers",
        paragraphs: [
          "Currently no third party receives data through cookies or similar technologies. If Google analytics were activated, that provider could carry out international data transfers covered by the mechanisms provided by the GDPR; this page would be updated to reflect it.",
        ],
      },
    ],
  },
};
